/**
 * Cube2D Component
 * 
 * A mathematically accurate 3D cube rendered using SVG with true isometric projection.
 * 
 * Only accepts dimension values (for labels), not positions.
 * All positioning and styling are predefined for consistency.
 */

import React from 'react';
import { Box } from '@mui/material';

interface Cube2DProps {
  side?: number; // Side length dimension (for label display)
  unit?: string; // Unit of measurement (default: 'cm')
  color?: string; // Fill color (default: '#e0e7ff')
  strokeColor?: string; // Stroke color (default: '#4f46e5')
}

export const Cube2D: React.FC<Cube2DProps> = ({
  side = 4,
  unit = 'cm',
  color = '#e0e7ff',
  strokeColor = '#4f46e5'
}) => {
  // Center the cube in the viewBox
  const centerX = 50;
  const centerY = 50;
  
  // Normalized size for visual consistency
  const size = 30; 
  
  // Isometric projection math
  const cos30 = 0.8660254;
  const sin30 = 0.5;
  
  const project = (x: number, y: number, z: number) => ({
    x: centerX + (x - y) * cos30,
    y: centerY + (x + y) * sin30 - z
  });
  
  // Calculate the 8 vertices of the cube
  // x goes right-down, y goes left-down, z goes straight up
  const p000 = project(0, 0, 0);       // Back-bottom
  const p100 = project(size, 0, 0);    // Right-bottom
  const p010 = project(0, size, 0);    // Left-bottom
  const p110 = project(size, size, 0); // Front-bottom
  const p001 = project(0, 0, size);    // Back-top
  const p101 = project(size, 0, size); // Right-top
  const p011 = project(0, size, size); // Left-top
  const p111 = project(size, size, size); // Front-top
  
  // Label position (below the cube)
  const labelX = centerX;
  const labelY = centerY + size + 12;
  
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', maxWidth: '320px', height: '320px', display: 'block' }}
      >
        {/* Hidden edges (dashed) */}
        <line x1={p000.x} y1={p000.y} x2={p100.x} y2={p100.y} stroke={strokeColor} strokeWidth={1} strokeDasharray="2,2" opacity={0.5} />
        <line x1={p000.x} y1={p000.y} x2={p010.x} y2={p010.y} stroke={strokeColor} strokeWidth={1} strokeDasharray="2,2" opacity={0.5} />
        <line x1={p000.x} y1={p000.y} x2={p001.x} y2={p001.y} stroke={strokeColor} strokeWidth={1} strokeDasharray="2,2" opacity={0.5} />
        
        {/* Visible Faces with different opacities for pseudo-shading */}
        {/* Top face */}
        <polygon
          points={`${p001.x},${p001.y} ${p101.x},${p101.y} ${p111.x},${p111.y} ${p011.x},${p011.y}`}
          fill={color}
          opacity={0.4}
        />
        {/* Left face */}
        <polygon
          points={`${p010.x},${p010.y} ${p110.x},${p110.y} ${p111.x},${p111.y} ${p011.x},${p011.y}`}
          fill={color}
          opacity={0.7}
        />
        {/* Right face */}
        <polygon
          points={`${p100.x},${p100.y} ${p110.x},${p110.y} ${p111.x},${p111.y} ${p101.x},${p101.y}`}
          fill={color}
          opacity={0.9}
        />

        {/* Visible Edges */}
        {/* Outer silhouette */}
        <polygon
          points={`${p011.x},${p011.y} ${p010.x},${p010.y} ${p110.x},${p110.y} ${p100.x},${p100.y} ${p101.x},${p101.y} ${p001.x},${p001.y}`}
          fill="none"
          stroke={strokeColor}
          strokeWidth={1.5}
          strokeLinejoin="round"
        />
        {/* Inner Y-edges connecting to front-top corner */}
        <line x1={p111.x} y1={p111.y} x2={p110.x} y2={p110.y} stroke={strokeColor} strokeWidth={1.5} strokeLinejoin="round" />
        <line x1={p111.x} y1={p111.y} x2={p011.x} y2={p011.y} stroke={strokeColor} strokeWidth={1.5} strokeLinejoin="round" />
        <line x1={p111.x} y1={p111.y} x2={p101.x} y2={p101.y} stroke={strokeColor} strokeWidth={1.5} strokeLinejoin="round" />
        
        {/* Corner points (visible vertices) */}
        <circle cx={p011.x} cy={p011.y} r={1.2} fill={strokeColor} />
        <circle cx={p010.x} cy={p010.y} r={1.2} fill={strokeColor} />
        <circle cx={p110.x} cy={p110.y} r={1.2} fill={strokeColor} />
        <circle cx={p100.x} cy={p100.y} r={1.2} fill={strokeColor} />
        <circle cx={p101.x} cy={p101.y} r={1.2} fill={strokeColor} />
        <circle cx={p001.x} cy={p001.y} r={1.2} fill={strokeColor} />
        <circle cx={p111.x} cy={p111.y} r={1.2} fill={strokeColor} />
        
        {/* Side length label */}
        <text
          x={labelX}
          y={labelY}
          fontSize={4.5}
          fill="#374151"
          textAnchor="middle"
          fontWeight={600}
        >
          s = {side} {unit}
        </text>
      </svg>
    </Box>
  );
};

export default Cube2D;
